import * as React from 'react'
import { GridEventListener } from '../../../models/events'
import { ReactTableRowId } from '../../../models/gridReactTable'
import { GridPrivateApiCommunity } from '../../../models/api/gridApiCommunity'
import { useGridApiEventHandler } from '../../utils/useGridApiEventHandler'
import { canUpdateFocus } from '../focus/useGridFocus'
import { useGridSelector } from '../../utils/useGridSelector'
import { gridDensityColumnFooterHeightSelector } from '../density/densitySelector'
import { DataGridProcessedProps } from '../../../models/props/DataGridProps'
import { GridCellModes } from '../../../models/gridEditRowModel'
import { isNavigationKey } from '../../../utils/keyboardUtils'
import gridClasses from '../../../style/styleMapper'
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '../../../colDef/gridCheckboxSelectionColDef'

const getLeftColumnIndex = ({
  currentColIndex,
  firstColIndex,
  lastColIndex,
  direction,
}: {
  currentColIndex: number
  firstColIndex: number
  lastColIndex: number
  direction: 'rtl' | 'ltr'
}) => {
  if (direction === 'rtl') {
    if (currentColIndex < lastColIndex) {
      return currentColIndex + 1
    }
  } else if (direction === 'ltr') {
    if (currentColIndex > firstColIndex) {
      return currentColIndex - 1
    }
  }
  return null
}

const getRightColumnIndex = ({
  currentColIndex,
  firstColIndex,
  lastColIndex,
  direction,
}: {
  currentColIndex: number
  firstColIndex: number
  lastColIndex: number
  direction: 'rtl' | 'ltr'
}) => {
  if (direction === 'rtl') {
    if (currentColIndex > firstColIndex) {
      return currentColIndex - 1
    }
  } else if (direction === 'ltr') {
    if (currentColIndex < lastColIndex) {
      return currentColIndex + 1
    }
  }
  return null
}

export const useGridKeyboardNavigation = (
  apiRef: React.MutableRefObject<GridPrivateApiCommunity>,
  props: Pick<DataGridProcessedProps, 'footerPlacement'>
): void => {
  const direction: 'rtl' | 'ltr' = 'ltr'

  let columnFooterHeight = useGridSelector(
    apiRef,
    gridDensityColumnFooterHeightSelector
  )
  if (props.footerPlacement === false) {
    columnFooterHeight = 0
  }

  const goToCell = React.useCallback(
    (
      colIndex: number,
      rowId: ReactTableRowId,
      direction: 'left' | 'right' | 'up' | 'down' = 'left'
    ) => {
      if (!rowId) {
        return
      }
      const leftColumns =
        apiRef.current.tableInstance.getLeftVisibleLeafColumns()

      const centerColumns =
        apiRef.current.tableInstance.getCenterVisibleLeafColumns()

      const rightColumns =
        apiRef.current.tableInstance.getRightVisibleLeafColumns()

      const visibleColumns = [
        ...leftColumns,
        ...centerColumns,
        ...rightColumns,
      ].map(one => one.id)

      const instances = apiRef.current.getVirtualizationInstances()

      const totalPinnedWidth =
        apiRef.current.tableInstance.getLeftTotalSize() +
        apiRef.current.tableInstance.getRightTotalSize()

      const rowIndex = apiRef.current.tableInstance.getRow(rowId).index
      const field = visibleColumns[colIndex]
      if (field) {
        if (direction === 'up' || direction === 'down') {
          const scrollStart = instances.virtualRow.scrollOffset

          const scrollEnd =
            // @ts-ignore
            scrollStart + instances.virtualRow.getSize() - columnFooterHeight
          const currentCellScrollPosition =
            instances.virtualRow.measurementsCache[rowIndex]

          if (
            (currentCellScrollPosition?.start ?? Infinity) < scrollStart ||
            (currentCellScrollPosition?.end ?? Infinity) > scrollEnd
          ) {
            if (direction === 'up') {
              instances.virtualRow.scrollToIndex(rowIndex, { align: 'start' })
            } else if (direction === 'down') {
              instances.virtualRow.scrollToIndex(rowIndex, { align: 'end' })
            }
          }
        } else if (direction === 'left' || direction === 'right') {
          const scrollStart = instances.virtualColumn.scrollOffset

          const scrollEnd =
            // @ts-ignore
            scrollStart + instances.virtualColumn.getSize() - totalPinnedWidth
          const newColIndex = colIndex - leftColumns.length
          if (newColIndex >= 0 && newColIndex <= centerColumns.length - 1) {
            const currentCellScrollPosition =
              instances.virtualColumn.measurementsCache[newColIndex]

            if (
              (currentCellScrollPosition?.start ?? Infinity) < scrollStart ||
              (currentCellScrollPosition?.end ?? Infinity) > scrollEnd
            ) {
              if (direction === 'left') {
                instances.virtualColumn.scrollToIndex(newColIndex, {
                  align: 'start',
                })
              } else if (direction === 'right') {
                instances.virtualColumn.scrollToIndex(newColIndex, {
                  align: 'end',
                })
              }
            }
          }
        }
        apiRef.current.setCellFocus(rowId, field)
      }
    },
    [apiRef, columnFooterHeight]
  )

  const goToHeader = React.useCallback(
    (
      colIndex: number,
      event: React.SyntheticEvent<Element>,
      direction: 'left' | 'right' = 'left'
    ) => {
      const leftColumns =
        apiRef.current.tableInstance.getLeftVisibleLeafColumns()

      const centerColumns =
        apiRef.current.tableInstance.getCenterVisibleLeafColumns()

      const rightColumns =
        apiRef.current.tableInstance.getRightVisibleLeafColumns()

      const visibleColumns = [
        ...leftColumns,
        ...centerColumns,
        ...rightColumns,
      ].map(one => one.id)

      const instances = apiRef.current.getVirtualizationInstances()

      const totalPinnedWidth =
        apiRef.current.tableInstance.getLeftTotalSize() +
        apiRef.current.tableInstance.getRightTotalSize()

      const field = visibleColumns[colIndex]

      if (field) {
        if (direction === 'left' || direction === 'right') {
          const scrollStart = instances.virtualColumn.scrollOffset

          const scrollEnd =
            // @ts-ignore
            scrollStart + instances.virtualColumn.getSize() - totalPinnedWidth
          const newColIndex = colIndex - leftColumns.length
          if (newColIndex >= 0 && newColIndex <= centerColumns.length - 1) {
            const currentCellScrollPosition =
              instances.virtualColumn.measurementsCache[newColIndex]

            if (
              (currentCellScrollPosition?.start ?? Infinity) < scrollStart ||
              (currentCellScrollPosition?.end ?? Infinity) > scrollEnd
            ) {
              if (direction === 'left') {
                instances.virtualColumn.scrollToIndex(newColIndex, {
                  align: 'start',
                })
              } else if (direction === 'right') {
                instances.virtualColumn.scrollToIndex(newColIndex, {
                  align: 'end',
                })
              }
            }
          }
        }
        apiRef.current.setColumnHeaderFocus(field, event)
      }
    },
    [apiRef]
  )

  const handleColumnHeaderKeyDown = React.useCallback<
    GridEventListener<'columnHeaderKeyDown'>
  >(
    (params, event) => {
      const headerTitleNode = event.currentTarget.querySelector(
        `.${gridClasses.columnHeaderTitleContainerContent}`
      )
      const isFromInsideContent =
        !!headerTitleNode &&
        headerTitleNode.contains(event.target as Node | null)

      if (
        isFromInsideContent &&
        params.field !== GRID_CHECKBOX_SELECTION_COL_DEF.id
      ) {
        // When focus is on a nested input, keyboard events have no effect to avoid conflicts with native events.
        // There is one exception for the checkBoxHeader
        return
      }

      const dimensions = apiRef.current.getRootDimensions()
      if (!dimensions) {
        return
      }
      const rows = apiRef.current.tableInstance.getRowModel().rows

      const leftColumns =
        apiRef.current.tableInstance.getLeftVisibleLeafColumns()

      const centerColumns =
        apiRef.current.tableInstance.getCenterVisibleLeafColumns()

      const rightColumns =
        apiRef.current.tableInstance.getRightVisibleLeafColumns()

      const visibleColumns = [
        ...leftColumns,
        ...centerColumns,
        ...rightColumns,
      ].map(one => one.id)

      const viewportPageSize =
        (apiRef.current.getVirtualizationInstances().virtualRow?.range
          ?.endIndex ?? 0) -
        (apiRef.current.getVirtualizationInstances().virtualRow?.range
          ?.startIndex ?? 0)
      const colIndexBefore = params.field
        ? visibleColumns.indexOf(params.field) ?? 0
        : 0
      const firstRowIndexInPage = 0
      const lastRowIndexInPage = rows.length - 1
      const firstColIndex = 0
      const lastColIndex = visibleColumns.length - 1

      let shouldPreventDefault = true

      switch (event.key) {
        case 'ArrowDown': {
          if (firstRowIndexInPage !== null) {
            const rowId = rows[firstRowIndexInPage]?.id
            if (rowId) {
              goToCell(colIndexBefore, rowId, 'down')
            }
          }
          break
        }

        case 'ArrowRight': {
          const rightColIndex = getRightColumnIndex({
            currentColIndex: colIndexBefore,
            firstColIndex,
            lastColIndex,
            direction: direction,
          })
          if (rightColIndex !== null) {
            goToHeader(rightColIndex, event, 'right')
          }

          break
        }

        case 'ArrowLeft': {
          const leftColIndex = getLeftColumnIndex({
            currentColIndex: colIndexBefore,
            firstColIndex,
            lastColIndex,
            direction: direction,
          })
          if (leftColIndex !== null) {
            goToHeader(leftColIndex, event, 'left')
          }
          break
        }

        case 'ArrowUp': {
          break
        }

        case 'PageDown': {
          if (firstRowIndexInPage !== null && lastRowIndexInPage !== null) {
            const rowId =
              rows[
                Math.min(
                  firstRowIndexInPage + viewportPageSize,
                  lastRowIndexInPage
                )
              ]?.id
            if (rowId) {
              goToCell(colIndexBefore, rowId, 'down')
            }
          }
          break
        }

        case 'Home': {
          goToHeader(firstColIndex, event)
          break
        }

        case 'End': {
          goToHeader(lastColIndex, event)
          break
        }

        case 'Enter': {
          break
        }

        case ' ': {
          // prevent Space event from scrolling
          break
        }

        default: {
          shouldPreventDefault = false
        }
      }

      if (shouldPreventDefault) {
        event.preventDefault()
      }
    },
    [apiRef, goToCell, direction, goToHeader]
  )

  const handleCellKeyDown = React.useCallback<GridEventListener<'cellKeyDown'>>(
    (params, event) => {
      // Ignore portal
      if (!event.currentTarget.contains(event.target as Element)) {
        return
      }

      // Get the most recent params because the cell mode may have changed by another listener
      const cellMode = apiRef.current.getCellMode(params.rowId, params.field)
      if (cellMode === GridCellModes.Edit || !isNavigationKey(event.key)) {
        return
      }

      const rows = apiRef.current.tableInstance.getRowModel().rows

      const _canUpdateFocus = canUpdateFocus(true, {
        event,
        cell: params,
      })

      if (!_canUpdateFocus) {
        return
      }

      const dimensions = apiRef.current.getRootDimensions()
      if (rows.length === 0 || !dimensions) {
        return
      }

      const viewportPageSize =
        (apiRef.current.getVirtualizationInstances().virtualRow?.range
          ?.endIndex ?? 0) -
        (apiRef.current.getVirtualizationInstances().virtualRow?.range
          ?.startIndex ?? 0)

      const leftColumns =
        apiRef.current.tableInstance.getLeftVisibleLeafColumns()

      const centerColumns =
        apiRef.current.tableInstance.getCenterVisibleLeafColumns()

      const rightColumns =
        apiRef.current.tableInstance.getRightVisibleLeafColumns()

      const visibleColumns = [
        ...leftColumns,
        ...centerColumns,
        ...rightColumns,
      ].map(one => one.id)

      const colIndexBefore = visibleColumns.indexOf(params.field) ?? 0
      const rowIndexBefore =
        apiRef.current.tableInstance.getRow(params.rowId)?.index ?? 0
      const firstRowIndexInPage = 0
      const lastRowIndexInPage = rows.length
      const firstColIndex = 0
      const lastColIndex = visibleColumns.length - 1

      let shouldPreventDefault = true

      switch (event.key) {
        case 'ArrowDown': {
          // "Enter" is only triggered by the row / cell editing feature
          if (rowIndexBefore < lastRowIndexInPage) {
            const rowIdFromIndex = rows[rowIndexBefore + 1]?.id
            goToCell(colIndexBefore, rowIdFromIndex!, 'down')
          }
          break
        }

        case 'ArrowUp': {
          if (rowIndexBefore > firstRowIndexInPage) {
            const rowIdFromIndex = rows[rowIndexBefore - 1]?.id
            goToCell(colIndexBefore, rowIdFromIndex!, 'up')
          } else {
            goToHeader(colIndexBefore, event)
          }
          break
        }

        case 'ArrowRight': {
          const rightColIndex = getRightColumnIndex({
            currentColIndex: colIndexBefore,
            firstColIndex,
            lastColIndex,
            direction,
          })
          if (rightColIndex !== null) {
            const rowIdFromIndex = rows[rowIndexBefore]?.id
            goToCell(rightColIndex, rowIdFromIndex!, 'right')
          }
          break
        }

        case 'ArrowLeft': {
          const leftColIndex = getLeftColumnIndex({
            currentColIndex: colIndexBefore,
            firstColIndex,
            lastColIndex,
            direction,
          })
          if (leftColIndex !== null) {
            const rowIdFromIndex = rows[rowIndexBefore]?.id
            goToCell(leftColIndex, rowIdFromIndex!, 'left')
          }
          break
        }

        case 'Tab': {
          // "Tab" is only triggered by the row / cell editing feature
          if (event.shiftKey && colIndexBefore > firstColIndex) {
            const rowIdFromIndex = rows[rowIndexBefore]?.id
            goToCell(colIndexBefore - 1, rowIdFromIndex!, 'left')
          } else if (!event.shiftKey && colIndexBefore < lastColIndex) {
            const rowIdFromIndex = rows[rowIndexBefore]?.id
            goToCell(colIndexBefore + 1, rowIdFromIndex!, 'right')
          }
          break
        }

        case ' ': {
          if (!event.shiftKey && rowIndexBefore < lastRowIndexInPage) {
            let rowIdFromIndex: any = Math.min(
              rowIndexBefore + viewportPageSize,
              lastRowIndexInPage
            )
            rowIdFromIndex = rows[rowIdFromIndex]?.id
            goToCell(colIndexBefore, rowIdFromIndex, 'down')
          }
          break
        }

        case 'PageDown': {
          if (rowIndexBefore < lastRowIndexInPage) {
            let rowIdFromIndex: any = Math.min(
              rowIndexBefore + viewportPageSize,
              lastRowIndexInPage
            )
            rowIdFromIndex = rows[rowIdFromIndex]?.id
            goToCell(colIndexBefore, rowIdFromIndex, 'down')
          }
          break
        }

        case 'PageUp': {
          // Go to the first row before going to header
          const nextRowIndex = Math.max(
            rowIndexBefore - viewportPageSize,
            firstRowIndexInPage
          )
          if (
            nextRowIndex !== rowIndexBefore &&
            nextRowIndex >= firstRowIndexInPage
          ) {
            const rowIdFromIndex = rows[nextRowIndex]?.id
            goToCell(colIndexBefore, rowIdFromIndex!, 'up')
          } else {
            goToHeader(colIndexBefore, event)
          }
          break
        }

        case 'Home': {
          if (event.ctrlKey || event.metaKey || event.shiftKey) {
            const rowIdFromIndex = rows[firstRowIndexInPage]?.id
            goToCell(firstColIndex, rowIdFromIndex!)
          } else {
            const rowIdFromIndex = rows[rowIndexBefore]?.id
            goToCell(firstColIndex, rowIdFromIndex!)
          }
          break
        }

        case 'End': {
          if (event.ctrlKey || event.metaKey || event.shiftKey) {
            const rowIdFromIndex = rows[lastRowIndexInPage]?.id
            goToCell(lastColIndex, rowIdFromIndex!)
          } else {
            const rowIdFromIndex = rows[rowIndexBefore]?.id
            goToCell(lastColIndex, rowIdFromIndex!)
          }
          break
        }

        default: {
          shouldPreventDefault = false
        }
      }

      if (shouldPreventDefault) {
        event.preventDefault()
      }
    },
    [apiRef, direction, goToCell, goToHeader]
  )

  useGridApiEventHandler(
    apiRef,
    'columnHeaderKeyDown',
    handleColumnHeaderKeyDown
  )
  useGridApiEventHandler(apiRef, 'cellKeyDown', handleCellKeyDown)
}
